// ─────────────────────────────────────────────────────────────────────────────
// RF-002 — Login del Programa de Reconocimiento (/login).
//
// Propuesta de reestructura (pendiente de visto bueno del cliente — ver la maqueta enviada para
// revisión): en vez de mostrar las dos rutas de golpe, `LoginForm` es una puerta de entrada con
// "Regístrate" / "Iniciar sesión" + el acceso con cuenta Sigma, y el paso `step === 'form'` es lo
// que ANTES se veía desde el principio (correo + contraseña, Frontline). El acceso con cuenta Sigma
// (MSAL real, loginRedirect) vive SOLO en la puerta de entrada — no se repite en el paso del
// formulario. El alta de Frontline vive en /app/registro, que desde este mismo paquete también
// ofrece el acceso con cuenta Sigma (ver `EntraBlock` en accessShared.tsx) para quien tiene correo
// institucional y no necesita registrarse. Estilo unificado con el registro (clases .acc-*, fondo
// degradado). Antes esta pantalla era una FACHADA de develop (nunca se renderizaba por el gate MSAL):
// mostraba llaves i18n crudas y sin estilo; aquí queda funcional y con presentación real.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  userAdminApi, useSsoDomains, useForgotPassword, useResendVerification,
  getRecognitionErrorDetail, getRecognitionErrorReason,
} from '@/api/recognition';
import { saveFrontlineSession } from '@/auth/frontlineSession';
import { markPostLogin } from '@/auth/postLoginLanding';
import { msalInstance } from '@/auth/msalConfig';
import { startCorporateLogin } from '@/auth/redirectRecovery';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { UserProvider } from '@/contexts/UserContext';
import {
  LanguageSelector, AuthLogo, AuthField, AuthHelp, AuthErrorModal, SolicitudPorCorreo, CorporateEmailNotice,
} from './recognition/access/accessShared';
import { esCorreoCorporativo, mensajeDeErrorDeLogin } from './recognition/access/accessErrors';
import './recognition/recognition.css';
import { withBase } from '@/utils/basePath';

function LoginForm() {
  const { t } = useTranslation('recognition');
  const navigate = useNavigate();
  // Propuesta de reestructura (pendiente de visto bueno del cliente, ver maqueta) — el login pasa
  // de mostrar TODO de una vez a una puerta de entrada con 2 botones ("Regístrate" / "Iniciar
  // sesión") + el acceso con cuenta Sigma; el formulario de correo/contraseña —lo que hoy se ve
  // desde el principio— queda detrás del botón "Iniciar sesión", con su propio "Volver".
  //
  // Estado LOCAL, no de ruta: la URL sigue siendo /login en los dos pasos (regla del cliente —
  // "solo debe revelar el formulario en la misma página"). Si el cliente pide quitar este paso
  // intermedio, basta con borrar este estado y el bloque `step === 'gate'` de abajo.
  // «Recuperar contraseña» del aviso del registro («esa nómina ya tiene cuenta») llega con ?recuperar=1: salta la
  // puerta y deja abierto «¿Olvidaste tu contraseña?».
  const [params] = useSearchParams();
  const abrirRecuperar = params.get('recuperar') === '1';
  const [step, setStep] = useState<'gate' | 'form'>(abrirRecuperar ? 'form' : 'gate');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // Igual que en el registro: hasta que no se sale del campo una vez, no se dice nada — marcar en
  // rojo lo que todavía se está tecleando es ruido.
  const [correoTocado, setCorreoTocado] = useState(false);
  // El MISMO criterio que el campo de correo del registro (FrontlineRegisterView).
  const emailValid = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(email.trim());
  // Las dos son 202 opacos: `isSuccess` significa «el servidor recibió la petición», nunca «el
  // correo salió». La pantalla enseña el mismo acuse en todos los casos.
  const forgot = useForgotPassword();
  const resend = useResendVerification();

  // Un correo corporativo en ESTE formulario es casi siempre alguien que no vio el acceso con cuenta Sigma.
  // El aviso NO bloquea el envío: ya hay cuentas Frontline creadas con un correo corporativo antes de que el
  // registro lo rechazara, y esas personas entran aquí con su contraseña. Si la lista no carga, no hay aviso.
  const ssoDomains = useSsoDomains();
  const correoCorporativo = esCorreoCorporativo(email, ssoDomains.data);

  // Si ya hay sesión corporativa (MSAL) —p.ej. al volver del redirect de Entra— al portal.
  useEffect(() => {
    if (msalInstance.getAllAccounts().length > 0) {
      markPostLogin();
      navigate('/app');
    }
  }, [navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError(t('login.errRequired', 'Ingresa tu correo y contraseña.'));
      return;
    }
    // 🔴 El aviso en línea sólo aparece tras SALIR del campo, así que quien envía directo desde el
    // teclado podía no haberlo visto nunca. Aquí se corta el envío y se dice lo mismo en el modal.
    // Sin esto la petición salía con un correo malformado y el servidor —que en el login NO valida
    // formato: `FrontlineLoginCommandValidator` sólo exige no vacío y ≤256— contestaba el 401 opaco
    // «correo o contraseña incorrectos», que señala a la contraseña cuando el problema es el correo.
    //
    // Cortar en el cliente es seguro, y se midió antes de cerrarlo (QA, consulta de solo lectura):
    // de las cuentas que hoy pueden iniciar sesión, NINGUNA tiene un correo que falle este criterio.
    // Además el criterio del servidor en el registro (`EmailFormatPolicy`) es MÁS estricto que esta
    // expresión, así que todo correo que pudo registrarse pasa por aquí.
    // El aviso se enseña SÓLO en línea, no también en el modal: con las dos vías el mismo texto
    // aparecía duplicado en pantalla. En línea es además donde este campo ya avisa, y no tapa el
    // campo que hay que corregir.
    if (!emailValid) {
      setCorreoTocado(true);
      setError(null);
      return;
    }
    setError(null);
    setPending(true);
    try {
      // Colaboradores SIN correo corporativo: auth por contraseña → JWT propio → sesión.
      const res = await userAdminApi.frontlineLogin({ correo: email.trim(), password });
      saveFrontlineSession(res.token, res.user);
      // Aterrizaje único tras el login (Brillamos con campaña activa, si no Reconocer): la carga
      // completa de abajo se lleva el estado de React, así que la marca viaja en sessionStorage.
      markPostLogin();

      // 🔴 CARGA COMPLETA, no `navigate('/app')`. Con la navegación de React Router el portal se
      // quedaba en el spinner para siempre y sólo entraba tras un F5 — sin error ni aviso, que es
      // lo que lo hacía difícil de ver (medido en QA el 2026-08-18).
      //
      // El porqué: `AppRouter` llama a `useCurrentUser()` POR ENCIMA de `<BrowserRouter>`, así que
      // un `navigate` —que sólo re-renderiza lo que cuelga del router— no lo vuelve a ejecutar.
      // Dentro de ese hook, la identidad corporativa sale de `useMsal()`, que es contexto REACTIVO
      // y sí despierta a `AppRouter`; la Frontline sale de `getFrontlineUser()`, una lectura suelta
      // de `localStorage` sin nada que avise. Resultado: `currentUser` seguía `null`,
      // `isLoadingProfile` seguía `true`, y el router pintaba `<PageSpinner/>` indefinidamente.
      //
      // Una carga completa remonta el árbol con la sesión ya escrita, que es justo lo que hacía el
      // F5 a mano. Es además el modo que ya usa toda esta zona de acceso (`<a href="/login">` en
      // registro, activación y verificación), y el correcto: al estrenar sesión conviene arrancar
      // limpio. La alternativa —hacer reactiva la sesión Frontline— toca la identidad de TODA la
      // app y no cabe en este arreglo; queda anotada como la corrección de fondo.
      window.location.assign(withBase('/app'));
    } catch (err) {
      // Contraseña TEMPORAL de un administrador: acertó la contraseña pero no hay sesión hasta que fije la definitiva.
      // El servidor entrega un enlace de un solo uso; se pasa a la pantalla de «elige tu contraseña» sin escribirlo en
      // el almacenamiento (esa pantalla lo saca de la barra de direcciones en cuanto lo lee).
      const cambioToken = getRecognitionErrorReason(err) === 'cambio-de-contrasena-obligatorio'
        ? getRecognitionErrorDetail(err, 'changePasswordToken')
        : undefined;
      if (cambioToken) {
        setPassword('');
        navigate(`/app/restablecer?token=${encodeURIComponent(cambioToken)}&origen=temporal`);
        return;
      }
      setError(mensajeDeErrorDeLogin(err, t));
    } finally {
      setPending(false);
    }
  };

  if (step === 'gate') {
    return (
      <div className="acc-form acc-gate">
        <p className="acc-alt acc-alt--top">
          {t('login.noAccountLede', '¿No tienes cuenta institucional?')}<br />
          {t('login.noAccountSub', 'Ingresa un correo personal')}
        </p>
        <a className="acc-register" href={withBase('/app/registro')}>{t('login.register', 'Regístrate')}</a>

        <p className="acc-alt">{t('login.alreadyRegisteredLede', '¿Ya te registraste?')}</p>
        <button type="button" className="acc-submit" onClick={() => setStep('form')}>
          {t('login.submit', 'Iniciar sesión')}
        </button>

        {/* Ruta Cuenta Sigma (Entra ID / SSO): clic explícito, sin auto-redirect (regla #2). Se
            queda SOLO en esta pantalla — regla del cliente: no se repite dentro del formulario de
            correo/contraseña, ya se vio aquí. */}
        <p className="acc-alt">{t('login.hasInstitutionalEmailLede', '¿Tienes un correo institucional?')}</p>
        <button
          type="button"
          className="acc-sigma"
          onClick={() => { void startCorporateLogin(); }}
        >
          <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
            <path fill="#f25022" d="M1 1h10v10H1z" />
            <path fill="#7fba00" d="M12 1h10v10H12z" />
            <path fill="#00a4ef" d="M1 12h10v10H1z" />
            <path fill="#ffb900" d="M12 12h10v10H12z" />
          </svg>
          {t('login.entra', 'Entra con correo Sigma')}
        </button>
      </div>
    );
  }

  return (
    <form className="acc-form" onSubmit={submit} noValidate>
      <button type="button" className="acc-back" onClick={() => setStep('gate')}>
        <i className="fas fa-arrow-left" aria-hidden="true" /> {t('login.back', 'Volver')}
      </button>

      {/* Ruta Frontline (sin correo institucional): correo + contraseña. */}
      <h2 className="acc-route-title">{t('login.frontlineRouteTitle', '¿No tienes correo institucional?')}</h2>
      <p className="acc-route-sub">{t('login.frontlineRouteSub', 'Entra con tu correo y contraseña.')}</p>
      <AuthField icon="fa-envelope" type="email" value={email} onChange={setEmail}
        placeholder={t('login.email', 'Correo')} autoComplete="username"
        onBlur={() => setCorreoTocado(true)} />
      {/* Mismo aviso que el registro: en línea bajo el campo (no en modal, que taparía justo lo
          que hay que corregir), sólo tras salir del campo, y desaparece solo al corregirlo. */}
      {correoTocado && email.trim() !== '' && !emailValid && (
        <p className="rp-error" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden="true" />{' '}
          {t('register.errEmail', 'Introduce un correo electrónico válido.')}
        </p>
      )}
      {correoCorporativo && (
        <CorporateEmailNotice
          message={t('login.corporateEmailHint', 'Este correo es corporativo: entra con tu cuenta Sigma, sin contraseña.')}
          onEnter={() => { void startCorporateLogin(); }}
        />
      )}
      <AuthField icon="fa-lock" withEye value={password} onChange={setPassword}
        placeholder={t('login.password', 'Contraseña')} autoComplete="current-password" />

      {/* Errores en modal (no debajo del formulario): un texto pequeño al fondo del formulario
          se confundía con el resto de leyendas de la pantalla y pasaba desapercibido. */}
      <AuthErrorModal message={error} onClose={() => setError(null)} />

      <button type="submit" className="acc-submit" disabled={pending}>
        {pending ? t('login.submitting', 'Ingresando…') : t('login.submit', 'Iniciar sesión')} <i className="fas fa-arrow-right" />
      </button>

      {/* Restablecimiento REAL. Sustituye al aviso «contacta a Capital Humano»: su premisa era
          que el Frontline no tenía un buzón fiable, y desde este paquete el correo del registro
          queda VERIFICADO — que es justo el canal correcto para restablecer. */}
      <SolicitudPorCorreo
        etiquetaEnlace={t('login.forgot', '¿Olvidaste tu contraseña?')}
        descripcion={t('login.forgotIntro', 'Escribe tu correo y te enviamos un enlace para crear una contraseña nueva.')}
        etiquetaBoton={t('login.forgotSubmit', 'Enviarme el enlace')}
        mensajeOpaco={t('login.forgotSent', 'Si ese correo está registrado, te enviamos un enlace. Caduca en 60 minutos.')}
        correoInicial={email}
        isAbiertoInicial={abrirRecuperar}
        enviando={forgot.isPending}
        onEnviar={(correo) => forgot.mutate(correo)}
      />

      {/* 🔴 SIEMPRE visible, no sólo tras un login fallido. Mostrarlo cuando el login falla por
          «correo sin verificar» diría, a quien lo pruebe, que esa cuenta existe y está a medias.
          El 401 del login es opaco a propósito; este enlace no puede deshacerlo. */}
      <SolicitudPorCorreo
        etiquetaEnlace={t('login.resendVerification', '¿No recibiste el correo de verificación?')}
        descripcion={t('login.resendIntro', 'Escribe tu correo y te enviamos otro enlace de verificación.')}
        etiquetaBoton={t('login.resendSubmit', 'Reenviar el correo')}
        mensajeOpaco={t('login.resendSent', 'Si tu cuenta está pendiente de verificar, te enviamos otro enlace.')}
        correoInicial={email}
        enviando={resend.isPending}
        onEnviar={(correo) => resend.mutate(correo)}
      />
    </form>
  );
}

function LoginContent() {
  const { t } = useTranslation('recognition');
  return (
    <div className="acc-page">
      <div className="acc-topbar"><LanguageSelector /></div>
      <div className="acc-card acc-card--compact">
        <AuthLogo />
        <h1 className="acc-title">{t('login.title', 'Iniciar sesión')}</h1>
     
        <LoginForm />
        <AuthHelp />
      </div>
    </div>
  );
}

export function LoginView() {
  return (
    <UserProvider>
      <ThemeProvider>
        <main className="acc-standalone">
          <LoginContent />
        </main>
      </ThemeProvider>
    </UserProvider>
  );
}

export default LoginView;
